import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import "../styles/NewsCarousel.css";

const formatDate = (value) => {
  if (!value) return "N/A";
  const raw = String(value).trim();
  let date;

  if (/^\d+$/.test(raw)) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "N/A";
    date = num > 1e12 ? new Date(num) : new Date(num * 1000);
  } else {
    const parsed = Date.parse(raw);
    if (Number.isNaN(parsed)) return raw;
    date = new Date(parsed);
  }

  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const categoryClass = (value) => {
  const label = String(value || "").toUpperCase();
  if (label.includes("CRIT")) return "news-critical";
  if (label.includes("NEG")) return "news-negative";
  if (label.includes("MUY")) return "news-strong";
  if (label.includes("POS")) return "news-positive";
  return "news-neutral";
};

const categoryLabel = (item) => {
  if (item?.categoria_usuario) return item.categoria_usuario;
  const label = String(item?.categoria || "").toUpperCase();
  if (label === "CRÍTICA") return "Riesgo crítico";
  if (label === "NEGATIVA") return "Precaución";
  if (label === "MUY POSITIVA") return "Muy favorable";
  if (label === "POSITIVA") return "Favorable";
  return "Neutral";
};

const getNewsImage = (item) => {
  const value = item?.image_url || item?.urlToImage || item?.image || item?.thumbnail || "";
  const url = String(value || "").trim();
  return /^https?:\/\//i.test(url) ? url : "";
};

const cleanText = (value, maxLength = 150) => {
  const text = String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trim()}...` : text;
};

const getNewsPriority = (item) => {
  const category = String(item?.categoria || "").toUpperCase();
  if (category.includes("CRIT")) return 5;
  if (category.includes("NEG")) return 4;
  if (category.includes("MUY")) return 3;
  if (category.includes("POS")) return 2;
  return 1;
};

const parseNewsTime = (value) => {
  if (!value) return 0;
  const raw = String(value).trim();
  if (/^\d+$/.test(raw)) {
    const num = Number(raw);
    return Number.isFinite(num) ? (num > 1e12 ? num : num * 1000) : 0;
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getMarketReading = (item) => {
  if (item?.lectura_usuario || item?.detalle_usuario) {
    const category = String(item?.categoria || "").toUpperCase();
    return {
      label: item?.lectura_usuario || categoryLabel(item),
      text: item?.detalle_usuario || "Lectura operativa generada por el análisis de noticias.",
      className: category.includes("CRIT")
        ? "reading-danger"
        : category.includes("NEG")
          ? "reading-warning"
          : category.includes("POS")
            ? "reading-positive"
            : "reading-neutral",
    };
  }

  const category = String(item?.categoria || "").toUpperCase();
  const score = Number(item?.score || 0);
  const buyRisk = String(item?.riesgo_compra_label || "").toUpperCase();
  const sellRisk = String(item?.riesgo_venta_label || "").toUpperCase();

  if (category.includes("CRIT") || (category.includes("NEG") && buyRisk === "ALTO")) {
    return {
      label: "Riesgo alto",
      text: "Evitar compras hasta que el contexto mejore.",
      className: "reading-danger",
    };
  }

  if (category.includes("NEG") || buyRisk === "MEDIO") {
    return {
      label: "Precaución",
      text: "Operar con menor exposición y esperar confirmación.",
      className: "reading-warning",
    };
  }

  if (category.includes("POS") || score >= 0.25 || sellRisk === "ALTO") {
    return {
      label: "Favorable",
      text: "Contexto noticioso acompaña una posible compra.",
      className: "reading-positive",
    };
  }

  return {
    label: "Neutral",
    text: "Sin señal fuerte; mantener monitoreo.",
    className: "reading-neutral",
  };
};

const getSummaryReading = (summary) => {
  const score = Number(summary?.score || 0);
  if (summary?.bloqueado) return { label: "Cautela activa", className: "risk-high" };
  if (score >= 0.3) return { label: "Entorno favorable", className: "risk-low" };
  if (score <= -0.2) return { label: "Entorno sensible", className: "risk-medium" };
  return { label: "Entorno neutral", className: "risk-low" };
};

const NewsCarousel = ({ data, loading, error }) => {
  const rawItems = useMemo(
    () => (Array.isArray(data?.predicciones_recientes) ? data.predicciones_recientes : []),
    [data?.predicciones_recientes]
  );
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const items = useMemo(() => {
    return [...rawItems].sort((a, b) => {
      const priorityDelta = getNewsPriority(b) - getNewsPriority(a);
      if (priorityDelta !== 0) return priorityDelta;
      return parseNewsTime(b?.fecha_noticia || b?.timestamp) - parseNewsTime(a?.fecha_noticia || a?.timestamp);
    });
  }, [rawItems]);

  const impactStats = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const category = String(item?.categoria || "").toUpperCase();
        if (category.includes("CRIT") || category.includes("NEG")) acc.caution += 1;
        else if (category.includes("POS") || category.includes("MUY")) acc.favorable += 1;
        else acc.neutral += 1;
        return acc;
      },
      { caution: 0, favorable: 0, neutral: 0 }
    );
  }, [items]);

  const summary = useMemo(() => {
    return {
      bloqueado: Boolean(data?.bloqueado),
      symbol: data?.symbol || "",
      ultimo: data?.ultimo_analisis || "",
      score: Number(data?.score_promedio || 0),
    };
  }, [data]);

  const summaryReading = useMemo(() => getSummaryReading(summary), [summary]);

  const scrollToIndex = useCallback((nextIndex) => {
    if (!trackRef.current || items.length === 0) return;
    const bounded = ((nextIndex % items.length) + items.length) % items.length;
    const track = trackRef.current;
    const node = track.querySelector(`[data-index="${bounded}"]`);
    if (!node) return;
    const left = node.offsetLeft - track.offsetLeft;
    track.scrollTo({ left, behavior: "smooth" });
    setActiveIndex(bounded);
  }, [items.length]);

  useEffect(() => {
    if (!items.length) return;
    if (paused) return;
    const timer = setInterval(() => {
      scrollToIndex(activeIndex + 1);
    }, 7000);
    return () => clearInterval(timer);
  }, [items.length, paused, activeIndex, scrollToIndex]);

  useEffect(() => {
    if (!items.length) return;
    setActiveIndex(0);
    scrollToIndex(0);
  }, [items.length, scrollToIndex]);

  if (loading) {
    return (
      <div className="news-carousel-wrapper">
        <div className="news-carousel-header">
          <h3>📰 Noticias en análisis</h3>
          <span className="news-carousel-sub">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="news-carousel-wrapper">
      <div className="news-carousel-header">
        <div className="news-carousel-title">
          <h3>📰 Noticias en análisis</h3>
          {summary.symbol ? <span className="news-carousel-symbol">{summary.symbol}</span> : null}
          {summary.bloqueado ? (
            <span className="news-carousel-badge blocked">Compras bloqueadas</span>
          ) : (
            <span className="news-carousel-badge ok">Compras permitidas</span>
          )}
        </div>
        <div className="news-carousel-meta">
          <span className="news-carousel-meta-item">
            Última sincronización: {summary.ultimo ? formatDate(summary.ultimo) : "N/A"}
          </span>
          <span className={`news-carousel-meta-item ${summaryReading.className}`}>
            Lectura general: {summaryReading.label}
          </span>
          {items.length ? (
            <span className="news-carousel-meta-item">
              Impacto: {impactStats.caution} cautela · {impactStats.favorable} favorable · {impactStats.neutral} neutral
            </span>
          ) : null}
        </div>
      </div>

      {error ? <div className="news-carousel-error">{error}</div> : null}

      {!items.length ? (
        <div className="news-carousel-empty">No hay noticias disponibles por ahora.</div>
      ) : (
        <div
          className="news-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            className="news-carousel-nav"
            onClick={() => scrollToIndex(activeIndex - 1)}
            aria-label="Anterior"
          >
            ‹
          </button>

          <div className="news-carousel-track" ref={trackRef}>
            {items.map((item, idx) => {
              const imageUrl = getNewsImage(item);
              const reading = getMarketReading(item);
              return (
                <div
                  key={`${item?.titulo || "news"}-${idx}`}
                  data-index={idx}
                  className={`news-carousel-card ${categoryClass(item?.categoria)}`}
                >
                  {imageUrl ? (
                    <div className="news-card-media">
                      <img
                        src={imageUrl}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(event) => {
                          if (event.currentTarget.dataset.fallback === "true") {
                            event.currentTarget.closest(".news-card-media")?.classList.add("is-hidden");
                            return;
                          }
                          event.currentTarget.dataset.fallback = "true";
                          event.currentTarget.src = "/immoneda.png";
                        }}
                      />
                    </div>
                  ) : null}
                  <div className="news-card-header">
                    <span className="news-card-category">{categoryLabel(item)}</span>
                    <span className="news-card-source">{item?.fuente || "Fuente"}</span>
                  </div>
                  <h4 className="news-card-title">{item?.titulo || "(Sin título)"}</h4>
                  {cleanText(item?.descripcion) ? (
                    <p className="news-card-description">{cleanText(item.descripcion)}</p>
                  ) : null}
                  <div className="news-card-time">{formatDate(item?.fecha_noticia || item?.timestamp)}</div>
                  <div className={`news-card-reading ${reading.className}`}>
                    <span className="news-card-reading-label">{reading.label}</span>
                    <span className="news-card-reading-text">{reading.text}</span>
                  </div>
                  {item?.url ? (
                    <a
                      className="news-card-link"
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir fuente
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>

          <button
            className="news-carousel-nav"
            onClick={() => scrollToIndex(activeIndex + 1)}
            aria-label="Siguiente"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsCarousel;
